// Signed SDP fingerprints — docs/03-security.md §4.1.
//
//   fingerprintSig = Ed25519-sign(identityKey,
//       "merkury-sdp-v1" || callId || senderUuid || recipientUuid || dtlsFingerprint)
//
// Binding callId and both UUIDs prevents replaying an old signature into a new
// call; the domain prefix (SIG_DOMAIN.SDP) prevents cross-purpose replay. The
// fingerprint is normalized before signing so both peers hash identical bytes
// regardless of their browser's SDP formatting.

import { SIG_DOMAIN } from '@merkury/protocol';
import { sha256 } from '@noble/hashes/sha2.js';
import { base64ToBytes, bytesToBase64, utf8 } from '../bytes.js';
import type { IdentitySigner } from '../types.js';
import { ed25519Verify } from './ed25519.js';

/**
 * SHA-256 that works with zero browser globals: WebCrypto when the runtime
 * provides `crypto.subtle` (browser/Node — identical output), else the pure-JS
 * @noble implementation (headless JSC, which has no `crypto.subtle`). Mirrors
 * the WebCrypto-preferred fallback idiom in ed25519.ts.
 */
async function sha256Digest(data: Uint8Array): Promise<Uint8Array> {
  const subtle = globalThis.crypto?.subtle;
  if (subtle) {
    const copy = new Uint8Array(new ArrayBuffer(data.length));
    copy.set(data);
    return new Uint8Array(await subtle.digest('SHA-256', copy));
  }
  return sha256(data);
}

/**
 * `callerKeyFingerprint` for `call.invite`: SHA-256 of the caller's raw
 * identity public key, base64 — a compact commitment the callee UI can show
 * or compare against its pin without shipping the whole key again.
 */
export async function publicKeyFingerprint(publicKeyB64: string): Promise<string> {
  const raw = base64ToBytes(publicKeyB64);
  if (!raw) throw new Error('invalid public key material');
  const digest = await sha256Digest(raw);
  return `sha256/${bytesToBase64(digest)}`;
}

/**
 * Extract the DTLS certificate fingerprint from an SDP blob (`a=fingerprint:`
 * line, session- or media-level). Returns the normalized form
 * `<hash-func-lowercase> <HEX:UPPER:CASE>`, or null when absent.
 */
export function extractDtlsFingerprint(sdp: string): string | null {
  const match = /^a=fingerprint:\s*(\S+)\s+(\S+)\s*$/im.exec(sdp);
  if (!match) return null;
  return `${match[1]!.toLowerCase()} ${match[2]!.toUpperCase()}`;
}

/** The exact bytes `fingerprintSig` is computed and checked over. */
export function sdpSigningBytes(
  callId: string,
  senderUuid: string,
  recipientUuid: string,
  dtlsFingerprint: string,
): Uint8Array {
  return utf8(SIG_DOMAIN.SDP + callId + senderUuid + recipientUuid + dtlsFingerprint);
}

/**
 * Sign the DTLS fingerprint of an outgoing SDP (invite/accept/renegotiate).
 * Throws if the SDP carries no fingerprint — sending unsigned SDP is never OK.
 */
export async function signSdpFingerprint(
  signer: IdentitySigner,
  args: { callId: string; toUuid: string; sdp: string },
): Promise<string> {
  const fingerprint = extractDtlsFingerprint(args.sdp);
  if (!fingerprint) throw new Error('outgoing SDP has no a=fingerprint line');
  const sig = await signer.sign(sdpSigningBytes(args.callId, signer.userId, args.toUuid, fingerprint));
  return bytesToBase64(sig);
}

/**
 * Verify an incoming SDP's `fingerprintSig` against the sender's pinned
 * identity public key. Any failure (missing fingerprint, malformed key or
 * signature, wrong bytes) is `false` — the caller must abort the call with a
 * security error, never degrade (docs/03 §4.1).
 */
export async function verifySdpFingerprint(args: {
  senderPublicKeyB64: string;
  callId: string;
  senderUuid: string;
  recipientUuid: string;
  sdp: string;
  fingerprintSigB64: string;
}): Promise<boolean> {
  const fingerprint = extractDtlsFingerprint(args.sdp);
  if (!fingerprint) return false;
  const publicKey = base64ToBytes(args.senderPublicKeyB64);
  const signature = base64ToBytes(args.fingerprintSigB64);
  if (!publicKey || !signature) return false;
  return ed25519Verify(
    publicKey,
    sdpSigningBytes(args.callId, args.senderUuid, args.recipientUuid, fingerprint),
    signature,
  );
}
